import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
    Plus, ArrowsCounterClockwise, Crown, ArrowRight, Link as LinkIcon, GitBranch,
} from "@phosphor-icons/react";

const KIND_TONE = {
    refinement: { bg: "bg-amber-50",  border: "border-amber-200", chip: "bg-amber-100 text-amber-800",   Icon: ArrowsCounterClockwise, label: "Refinement" },
    retainer:   { bg: "bg-violet-50", border: "border-violet-200", chip: "bg-violet-100 text-violet-800", Icon: Crown,                  label: "Retainer" },
};

/**
 * LinkedCasesPanel — shows linked children (refinements + retainers) of a
 * parent case and lets the doctor spawn new ones.
 *
 * Spawn flow (2026-02-27 change — was inline 3-field dialog):
 *  • Click "Request refinement" / "Order retainer" → POST creates a CHILD
 *    case at status=draft with patient + doctor pre-filled and parent's
 *    case files auto-copied into the new draft's records.
 *  • We then navigate the doctor to `/doctor/cases/new?draft=<child_id>`,
 *    which resumes the standard case-submission wizard against that draft.
 *    The doctor fills clinical + prescription + replaces any records that
 *    are stale, then submits — at which point the case enters the planner
 *    intake queue exactly like a primary case.
 *
 * Endpoints used:
 *   GET  /api/cases/{id}/children
 *   POST /api/cases/{id}/refinement   (body: { note? }) → returns draft child
 *   POST /api/cases/{id}/retainer     (body: { note?, arches?, set_count? }) → returns draft child
 */
export function LinkedCasesPanel({ parentCase, onChange }) {
    const navigate = useNavigate();
    const [children, setChildren] = useState([]);
    const [busyKind, setBusyKind] = useState(null);

    const canSpawn = parentCase
        && ["in_treatment", "treatment_started", "completed"].includes(parentCase.status)
        && !parentCase.parent_case_id;

    const load = () => {
        if (!parentCase?.id) return;
        api.get(`/cases/${parentCase.id}/children`)
            .then((r) => setChildren(r.data || []))
            .catch(() => setChildren([]));
    };
    useEffect(() => { load(); }, [parentCase?.id]); // eslint-disable-line react-hooks/exhaustive-deps

    const spawn = async (kind) => {
        if (busyKind) return;
        setBusyKind(kind);
        try {
            // Empty body — the wizard collects all the clinical detail.
            // The endpoint already inherits patient + doctor from parent
            // and auto-copies the parent's records into the new draft.
            const r = await api.post(`/cases/${parentCase.id}/${kind}`, {});
            const child = r.data;
            toast.success(
                kind === "refinement"
                    ? `Refinement draft ${child.code} created — fill the case form to submit.`
                    : `Retainer draft ${child.code} created — fill the case form to submit.`
            );
            onChange?.();
            navigate(`/doctor/cases/new?draft=${child.id}`);
        } catch (e) {
            toast.error(e.response?.data?.detail || `Failed to create ${kind}`);
        } finally {
            setBusyKind(null);
        }
    };

    if (!parentCase) return null;
    // Hide entire panel on cases that can't have children AND have none
    if (!canSpawn && children.length === 0 && !parentCase.parent_case_id) return null;

    return (
        <div className="rounded-2xl border border-border bg-card overflow-hidden" data-testid="linked-cases-panel">
            <div className="flex items-center justify-between px-5 py-4 border-b border-border bg-secondary/20">
                <div className="flex items-center gap-2">
                    <GitBranch size={16} weight="duotone" className="text-primary" />
                    <div className="font-display font-bold text-sm">
                        Linked cases {children.length > 0 && <span className="text-muted-foreground text-xs font-normal">· {children.length}</span>}
                    </div>
                </div>
                {canSpawn && (
                    <div className="flex items-center gap-1.5">
                        <Button
                            size="sm"
                            variant="outline"
                            className="rounded-full bg-amber-50 border-amber-200 text-amber-900 hover:bg-amber-100"
                            onClick={() => spawn("refinement")}
                            disabled={busyKind !== null}
                            data-testid="linked-create-refinement"
                        >
                            <ArrowsCounterClockwise size={11} className="mr-1" />
                            {busyKind === "refinement" ? "Starting…" : "Request refinement"}
                        </Button>
                        <Button
                            size="sm"
                            variant="outline"
                            className="rounded-full bg-violet-50 border-violet-200 text-violet-900 hover:bg-violet-100"
                            onClick={() => spawn("retainer")}
                            disabled={busyKind !== null}
                            data-testid="linked-create-retainer"
                        >
                            <Crown size={11} className="mr-1" />
                            {busyKind === "retainer" ? "Starting…" : "Order retainer"}
                        </Button>
                    </div>
                )}
            </div>

            {/* Helper one-liner so the doctor understands what happens on click */}
            {canSpawn && children.length === 0 && (
                <div className="px-5 py-3 text-xs text-muted-foreground bg-secondary/10 border-b border-border">
                    Either action opens the standard case form on a fresh draft
                    — patient + provider are pre-filled and your previous scans
                    are attached as a starting point. Submit when ready and it
                    routes back to admin like any new case.
                </div>
            )}

            {children.length === 0 ? (
                <div className="px-5 py-6 text-center text-muted-foreground text-sm">
                    No refinements or retainers linked to this case yet.
                </div>
            ) : (
                <div>
                    {children.map((c) => {
                        const tone = KIND_TONE[c.case_kind] || KIND_TONE.refinement;
                        return (
                            <button
                                key={c.id}
                                type="button"
                                onClick={() => {
                                    // Drafts open straight into the wizard so the
                                    // doctor can resume; everything else opens the
                                    // case-detail page.
                                    if (c.status === "draft") {
                                        navigate(`/doctor/cases/new?draft=${c.id}`);
                                    } else {
                                        navigate(`/doctor/cases/${c.id}`);
                                    }
                                }}
                                className="w-full flex items-center justify-between px-5 py-3 border-b border-border last:border-0 hover:bg-secondary/30 transition text-left"
                                data-testid={`linked-child-${c.id}`}
                            >
                                <div className="flex items-center gap-3 min-w-0">
                                    <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${tone.bg} border ${tone.border}`}>
                                        <tone.Icon size={14} weight="fill" className="text-foreground/80" />
                                    </div>
                                    <div className="min-w-0">
                                        <div className="flex items-center gap-2">
                                            <span className="font-mono text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{c.code}</span>
                                            <span className={`text-[9px] uppercase tracking-wide font-bold px-1.5 py-0.5 rounded ${tone.chip}`}>
                                                {tone.label}
                                            </span>
                                            {c.status === "draft" && (
                                                <span className="text-[9px] uppercase tracking-wide font-bold px-1.5 py-0.5 rounded bg-zinc-200 text-zinc-700">
                                                    Draft
                                                </span>
                                            )}
                                        </div>
                                        <div className="text-xs font-semibold text-foreground">
                                            {c.status?.replace(/_/g, " ")}
                                        </div>
                                    </div>
                                </div>
                                <div className="text-xs text-muted-foreground flex items-center gap-1.5">
                                    {c.created_at?.slice(0, 10)}
                                    <ArrowRight size={11} />
                                </div>
                            </button>
                        );
                    })}
                </div>
            )}

            {/* Parent link — only shown if this IS a child case */}
            {parentCase.parent_case_id && (
                <div className="px-5 py-3 border-t border-border bg-secondary/20 text-xs flex items-center gap-2">
                    <LinkIcon size={12} className="text-primary" />
                    <span className="text-muted-foreground">Parent case:</span>
                    <button
                        type="button"
                        onClick={() => navigate(`/doctor/cases/${parentCase.parent_case_id}`)}
                        className="font-mono font-semibold text-primary hover:underline"
                        data-testid="linked-parent-link"
                    >
                        {parentCase.parent_case_code || parentCase.parent_case_id?.slice(0, 8)}
                    </button>
                </div>
            )}
        </div>
    );
}

export default LinkedCasesPanel;
